'use client';

import { APPLIANCE_TYPES, type ApplianceType } from '@slims/shared/energy';
import { Plus, Trash2 } from 'lucide-react';
import { type ApplianceDraft, EMPTY_APPLIANCE_DRAFT } from './appliance-draft';

export interface AppliancesEditorProps {
	rows: ApplianceDraft[];
	onChange: (rows: ApplianceDraft[]) => void;
}

const INPUT_CLASS =
	'w-full rounded-lg border border-border bg-background px-3 py-1.5 text-[13px] text-text-primary focus:border-accent focus:outline-none';
const LABEL_CLASS = 'text-[11px] text-text-muted';

const CASA = APPLIANCE_TYPES.filter((t) => t.ambito === 'casa' && t.value !== 'altro');
const ATTIVITA = APPLIANCE_TYPES.filter((t) => t.ambito === 'attivita');

/** I dispositivi elettrici al prelievo: tipo, quanti, e la potenza di ognuno se si sa. */
export function AppliancesEditor({ rows, onChange }: AppliancesEditorProps) {
	const update = (i: number, patch: Partial<ApplianceDraft>) =>
		onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

	return (
		<div className="space-y-2">
			{rows.length === 0 && (
				<p className="text-[12px] text-text-muted">Nessun dispositivo indicato.</p>
			)}
			{rows.map((row, i) => (
				<div key={i} className="grid grid-cols-[minmax(0,2fr)_80px_110px_32px] items-end gap-2">
					<label className="space-y-1">
						<span className={LABEL_CLASS}>Dispositivo</span>
						<select
							value={row.tipo}
							onChange={(e) => update(i, { tipo: e.target.value as ApplianceType | '' })}
							className={INPUT_CLASS}
						>
							<option value="" disabled>
								Scegli…
							</option>
							<optgroup label="Casa">
								{CASA.map((t) => (
									<option key={t.value} value={t.value}>
										{t.label}
									</option>
								))}
							</optgroup>
							<optgroup label="Attività">
								{ATTIVITA.map((t) => (
									<option key={t.value} value={t.value}>
										{t.label}
									</option>
								))}
							</optgroup>
							<option value="altro">Altro</option>
						</select>
						{row.tipo === 'altro' && (
							<input
								aria-label="Che dispositivo è"
								value={row.nome}
								onChange={(e) => update(i, { nome: e.target.value })}
								placeholder="Che cos'è"
								className={INPUT_CLASS}
							/>
						)}
					</label>
					<label className="space-y-1 self-start">
						<span className={LABEL_CLASS}>Quanti</span>
						<input
							inputMode="numeric"
							value={row.quantita}
							onChange={(e) => update(i, { quantita: e.target.value })}
							className={INPUT_CLASS}
						/>
					</label>
					<label className="space-y-1 self-start">
						<span className={LABEL_CLASS}>Potenza (kW)</span>
						<input
							inputMode="decimal"
							value={row.potenzaKw}
							onChange={(e) => update(i, { potenzaKw: e.target.value })}
							placeholder="se la sai"
							className={INPUT_CLASS}
						/>
					</label>
					<button
						type="button"
						aria-label="Togli il dispositivo"
						onClick={() => onChange(rows.filter((_, j) => j !== i))}
						className="mt-5 self-start rounded-md p-1.5 text-text-muted hover:text-danger"
					>
						<Trash2 className="h-4 w-4" />
					</button>
				</div>
			))}
			<button
				type="button"
				onClick={() => onChange([...rows, EMPTY_APPLIANCE_DRAFT])}
				className="inline-flex items-center gap-1 text-[12px] font-semibold text-accent hover:underline"
			>
				<Plus className="h-3.5 w-3.5" /> Aggiungi dispositivo
			</button>
		</div>
	);
}
